import { useCallback, useEffect, useState } from 'react';
import { logger } from '../../../utils/logger';
import { telegramApi } from '../../../utils/telegramApi';

/**
 * Хук для управления камерами устройства.
 * 
 * Ответственность:
 * - Инициализация списка доступных камер
 * - Переключение между передней и задней камерой
 * - Обработка ошибок доступа к камере
 * - Формирование видео-ограничений для WebRTC
 * 
 * @returns Состояние камер и методы управления
 */
export function useCameraDevices() {
    const [cameras, setCameras] = useState<MediaDeviceInfo[]>([]);
    const [isFrontCamera, setIsFrontCamera] = useState(false);
    const [retryAttempt, setRetryAttempt] = useState(0);

    // Получаем список доступных камер при загрузке
    useEffect(() => {
        const getCameras = async () => {
            try {
                const devices = await navigator.mediaDevices.enumerateDevices();
                const videoDevices = devices.filter(device => device.kind === 'videoinput');
                
                if (videoDevices.length === 0) {
                    logger.error('Не найдено ни одно медиаустройство камеры');
                    telegramApi.showAlert('Не удалось найти камеру. Приложение будет закрыто.').then(() => {
                        telegramApi.closeApp();
                    });
                    return;
                }
                
                setCameras(videoDevices);
            } catch (error) {
                logger.error('Ошибка получения списка камер', { error });
                telegramApi.showAlert('Не удалось получить доступ к камере. Приложение будет закрыто.').then(() => {
                    telegramApi.closeApp();
                });
            }
        };
        getCameras();
    }, []);

    /**
     * Переключает между передней и задней камерами устройства.
     */
    const switchCamera = useCallback(() => {
        setIsFrontCamera(!isFrontCamera);
    }, [isFrontCamera]);

    /**
     * Повторно инициализирует камеру после ошибки.
     */
    const retryCamera = useCallback(() => {
        setRetryAttempt(v => v + 1);
    }, []);

    const videoConstraints = {
        width: { min: 640, ideal: 960, max: 1280 },
        height: { min: 480, ideal: 540, max: 720 },
        aspectRatio: 4/3,
        facingMode: { ideal: isFrontCamera ? 'user' : 'environment' }
    };

    return {
        cameras,
        isFrontCamera,
        retryAttempt,
        videoConstraints,
        switchCamera,
        retryCamera
    };
}